import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import mermaid from 'mermaid';

// Initialize mermaid with white text on colored backgrounds for readability
mermaid.initialize({
  startOnLoad: false,
  theme: 'base',
  themeVariables: {
    // Node colors - slate gray background with white text
    primaryColor: '#475569',
    primaryTextColor: '#ffffff',
    primaryBorderColor: '#334155',
    // Secondary nodes (decisions/diamonds)
    secondaryColor: '#64748b',
    secondaryTextColor: '#ffffff',
    secondaryBorderColor: '#475569',
    // Tertiary
    tertiaryColor: '#94a3b8',
    tertiaryTextColor: '#1e293b',
    tertiaryBorderColor: '#64748b',
    // Lines and edges
    lineColor: '#64748b',
    // Font
    fontFamily: 'system-ui, -apple-system, sans-serif',
    fontSize: '14px',
  },
});

interface Section {
  id: string;
  title: string;
  icon: React.ReactNode;
}

const sections: Section[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
  {
    id: 'daily-workflow',
    title: 'Daily Workflow',
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    id: 'calls-sets',
    title: 'Calls & Sets',
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
      </svg>
    ),
  },
  {
    id: 'time-tracking',
    title: 'Time Tracking',
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    id: 'store-visits',
    title: 'Store Visits',
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    ),
  },
  {
    id: 'expenses',
    title: 'Expenses',
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
  },
  {
    id: 'admin-features',
    title: 'Admin',
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
  },
  {
    id: 'troubleshooting',
    title: 'Troubleshooting',
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
];

// Mermaid diagram component - stores rendered SVG in state to prevent flash on re-render
function MermaidDiagram({ chart, id }: { chart: string; id: string }) {
  const [svg, setSvg] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const renderAttemptRef = useRef(0);

  useEffect(() => {
    let mounted = true;
    const currentAttempt = ++renderAttemptRef.current;

    const renderDiagram = async () => {
      try {
        // Use unique ID for each render attempt to avoid mermaid ID conflicts
        const uniqueId = `mermaid-${id}-${currentAttempt}-${Date.now()}`;
        const result = await mermaid.render(uniqueId, chart);
        if (mounted && currentAttempt === renderAttemptRef.current) {
          setSvg(result.svg);
          setError(null);
        }
      } catch (err) {
        console.error('Mermaid rendering error:', err);
        if (mounted && currentAttempt === renderAttemptRef.current) {
          setError('Failed to render diagram');
        }
      }
    };

    renderDiagram();

    return () => {
      mounted = false;
    };
  }, [chart, id]);

  if (error) {
    return <p className="text-red-500 text-sm my-4 text-center">{error}</p>;
  }

  if (!svg) {
    return (
      <div className="my-4 flex justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div
      className="my-4 flex justify-center overflow-x-auto"
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

export default function Help() {
  const [activeSection, setActiveSection] = useState('getting-started');

  // Deep-link support: /help#troubleshooting switches to that section on mount.
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash && sections.some((s) => s.id === hash)) {
      setActiveSection(hash);
    }
  }, []);

  const dailyWorkflowChart = `
flowchart TD
    A[Open RDM App] --> B[View Today Tab]
    B --> C[Tap Clock In]
    C --> D{Sets or Calls Today?}
    D -->|Sets| E[Go to Scheduled Set]
    D -->|Calls| F[Pick from Available Calls]
    E --> G[Travel to Store]
    F --> G
    G --> H[GPS Check In]
    H --> I[Complete Store Tasks]
    I --> J[Photos Surveys and Capture]
    J --> K[Tap Leave Store]
    K --> L[Get Manager Signature]
    L --> M{More Work?}
    M -->|Yes| D
    M -->|No| N[Review Time Entries]
    N --> O[Submit Expenses]
    O --> P[Tap Clock Out]
  `;

  const timeTrackingChart = `
flowchart TD
    A[Tap Clock In on Today] --> B[Confirm Odometer]
    B --> C[Travel to Store]
    C --> D[GPS Check In Near Store]
    D --> E[Green In Store Banner Shows]
    E --> F[Complete Store Tasks]
    F --> G[Tap Leave Store]
    G --> H[Manager Signs on Screen]
    H --> I[Time Entry Created]
    I --> J{More Stores?}
    J -->|Yes| C
    J -->|No| K[Tap Clock Out]
    K --> L{Going Back Out Later?}
    L -->|Yes| A
    L -->|No| M[Day Complete]
  `;

  const expenseChart = `
flowchart TD
    A[Incur Expense] --> B[Keep Receipt]
    B --> C[Open Expenses Tab]
    C --> D[Tap Add Expense]
    D --> E[Select Category]
    E --> F[Enter Amount]
    F --> G[Take Photo of Receipt]
    G --> H[Add Description]
    H --> I[Submit Expense]
    I --> J[Awaits Approval]
  `;

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Header */}
      <header className="bg-gradient-to-r from-primary-600 to-primary-700 px-4 pb-6 pt-12 text-white">
        <div className="flex items-center gap-3">
          <Link
            to="/resources"
            className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20 transition-colors hover:bg-white/30"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <div>
            <h1 className="text-2xl font-bold">Help Guide</h1>
            <p className="text-sm text-white/80">RDM Connect Mobile App</p>
          </div>
        </div>
      </header>

      {/* Section Navigation */}
      <div className="overflow-x-auto bg-white px-4 py-3 shadow-sm">
        <div className="flex gap-2">
          {sections.map((section) => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                activeSection === section.id
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {section.icon}
              {section.title}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <main className="p-4">
        {/* Getting Started */}
        {activeSection === 'getting-started' && (
          <div className="space-y-4">
            <div className="rounded-xl bg-white p-6 shadow-card">
              <h2 className="mb-4 text-xl font-bold text-slate-800">Welcome to RDM Connect</h2>
              <p className="mb-4 text-slate-600">
                RDM Connect is your mobile companion for field merchandising operations. This app helps you manage your daily store visits, track time, submit expenses, and access important resources.
              </p>

              <h3 className="mb-3 mt-6 text-lg font-semibold text-slate-800">App Navigation</h3>
              <p className="mb-4 text-slate-600">
                The app has five main sections accessible from the bottom navigation bar:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100 text-blue-600">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                  </div>
                  <div>
                    <span className="font-medium text-slate-800">Today</span>
                    <p className="text-sm text-slate-500">Your daily hub - clock in/out, available calls, and active store visits</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-purple-100 text-purple-600">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <span className="font-medium text-slate-800">Schedule</span>
                    <p className="text-sm text-slate-500">Calendar views (Day/Week/Month), call progress tracking, and scheduled sets</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-green-100 text-green-600">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <span className="font-medium text-slate-800">Time</span>
                    <p className="text-sm text-slate-500">View and manage your time entries and hours worked</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-600">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <div>
                    <span className="font-medium text-slate-800">Expenses</span>
                    <p className="text-sm text-slate-500">Submit and track expense reimbursements with receipt photos</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z" />
                    </svg>
                  </div>
                  <div>
                    <span className="font-medium text-slate-800">More</span>
                    <p className="text-sm text-slate-500">Profile, My Calls, My Sets, Resources, Help, and settings</p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="rounded-xl bg-white p-6 shadow-card">
              <h3 className="mb-3 text-lg font-semibold text-slate-800">First Time Setup</h3>
              <ol className="space-y-3 text-slate-600">
                <li className="flex gap-3">
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-bold text-primary-700">1</span>
                  <span>Log in with your RDM credentials (same as the web portal)</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-bold text-primary-700">2</span>
                  <span>Allow location access when prompted (required for clock in/out)</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-bold text-primary-700">3</span>
                  <span>Enable notifications to receive assignment updates</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-bold text-primary-700">4</span>
                  <span>Review your assigned stores in the Today tab</span>
                </li>
              </ol>
            </div>

            <div className="rounded-xl bg-white p-6 shadow-card">
              <h3 className="mb-3 text-lg font-semibold text-slate-800">Works Offline</h3>
              <p className="mb-3 text-slate-600">
                RDM Connect is built to work without a signal. Check-ins, photos, surveys, product captures, and time
                entries are saved on your device first and sync to the office automatically when you're back online —
                so a dead zone in the back of a store never costs you your work.
              </p>
              <ul className="space-y-2 text-slate-600">
                <li>- The status chip at the top shows <strong>Live</strong>, <strong>Syncing</strong>, or <strong>Offline</strong></li>
                <li>- Tap it to see what's synced today, what's still saved on this device, and a <strong>Sync Now</strong> button</li>
                <li>- If something can't sync, a <strong>Review sync issues</strong> link appears there</li>
              </ul>
            </div>
          </div>
        )}

        {/* Daily Workflow */}
        {activeSection === 'daily-workflow' && (
          <div className="space-y-4">
            <div className="rounded-xl bg-white p-6 shadow-card">
              <h2 className="mb-4 text-xl font-bold text-slate-800">Your Daily Workflow</h2>
              <p className="mb-4 text-slate-600">
                Follow this workflow for a typical day of store visits. The diagram below shows the complete process from start to finish.
              </p>
              <div className="rounded-lg bg-slate-50 p-4">
                <MermaidDiagram chart={dailyWorkflowChart} id="daily-workflow" />
              </div>
            </div>

            <div className="rounded-xl bg-white p-6 shadow-card">
              <h3 className="mb-4 text-lg font-semibold text-slate-800">Step-by-Step Guide</h3>

              <div className="space-y-4">
                <div className="rounded-lg border border-slate-200 p-4">
                  <h4 className="mb-2 font-medium text-slate-800">1. Start Your Day</h4>
                  <p className="text-sm text-slate-600">
                    Open the app, go to the <strong>Today</strong> tab, and tap the <strong>Clock In</strong> button. This begins tracking your work time. Check the <strong>Schedule</strong> tab to see your call progress and any scheduled sets.
                  </p>
                </div>

                <div className="rounded-lg border border-slate-200 p-4">
                  <h4 className="mb-2 font-medium text-slate-800">2. Choose Your Work</h4>
                  <p className="text-sm text-slate-600">
                    <strong>Sets</strong> have specific dates - prioritize these first. <strong>Calls</strong> are flexible - pick from available stores based on location. Check the Schedule page for light days to fit in extra calls.
                  </p>
                </div>

                <div className="rounded-lg border border-slate-200 p-4">
                  <h4 className="mb-2 font-medium text-slate-800">3. Navigate to Store</h4>
                  <p className="text-sm text-slate-600">
                    Tap on a store card to view details. Use the <strong>Directions</strong> button to open navigation in Google Maps or Apple Maps.
                  </p>
                </div>

                <div className="rounded-lg border border-slate-200 p-4">
                  <h4 className="mb-2 font-medium text-slate-800">4. GPS Check In</h4>
                  <p className="text-sm text-slate-600">
                    When you arrive at the store, tap the <strong>Check In with GPS</strong> button. The app verifies you are within 200 meters of the store. A green <strong>In Store</strong> banner will appear on the Today page.
                  </p>
                </div>

                <div className="rounded-lg border border-slate-200 p-4">
                  <h4 className="mb-2 font-medium text-slate-800">5. Complete Store Tasks</h4>
                  <p className="text-sm text-slate-600">
                    Follow each project's requirements: take photos and documents, complete surveys, and capture the
                    project's onsite work — placing an <strong>order</strong>, counting <strong>on-hand inventory</strong>,
                    and/or scanning <strong>out-of-stock</strong> shelf tags. The capture button shows only what the
                    project needs. After you <strong>save an order</strong>, the app takes you straight back to the call —
                    no need to press Back. A single visit can cover several calls or sets — switch between them and
                    pause/resume without losing progress. (See <strong>Store Visits</strong> for details.)
                  </p>
                </div>

                <div className="rounded-lg border border-slate-200 p-4">
                  <h4 className="mb-2 font-medium text-slate-800">6. Leave Store</h4>
                  <p className="text-sm text-slate-600">
                    When finished, tap <strong>Leave Store</strong>. You will see a checkout summary showing your time and projects. Under each project it lists what you captured — <strong>orders</strong> (with quantities), <strong>inventory</strong> counts (anything 3 or under is flagged <strong>LOW</strong>), and <strong>out-of-stock</strong> items — so the manager can review the work before approving. Ask the store manager to sign on screen, or tap <strong>No Signature</strong> if unavailable and provide a reason.
                  </p>
                </div>

                <div className="rounded-lg border border-slate-200 p-4">
                  <h4 className="mb-2 font-medium text-slate-800">7. End Your Day</h4>
                  <p className="text-sm text-slate-600">
                    After visiting all stores, return to the <strong>Today</strong> tab and tap <strong>Clock Out</strong>. Review your time entries in the <strong>Time</strong> tab and submit any expenses before the pay period ends.
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-xl bg-white p-6 shadow-card">
              <h3 className="mb-3 text-lg font-semibold text-slate-800">Unscheduled Work (Live or After the Fact)</h3>
              <p className="mb-3 text-slate-600">
                Covering a store that isn't on your schedule, or missed recording a visit? Tap{' '}
                <strong>Working somewhere unscheduled?</strong> at the bottom of the <strong>Today</strong> tab. If
                you're at the store right now, pick the store, project and reason and tap{' '}
                <strong>Check in now</strong> — your visit starts immediately and checkout records the end. For a
                visit that already happened, continue to details and enter your time in / out; either way you complete
                the same survey, photo, and product-capture steps as any visit.
              </p>
              <p className="text-sm text-slate-500">
                Manual entries are flagged so the office knows they weren't GPS-verified in real time.
              </p>
            </div>
          </div>
        )}

        {/* Time Tracking */}
        {activeSection === 'time-tracking' && (
          <div className="space-y-4">
            <div className="rounded-xl bg-white p-6 shadow-card">
              <h2 className="mb-4 text-xl font-bold text-slate-800">Time Tracking</h2>
              <p className="mb-4 text-slate-600">
                Accurate time tracking is essential for payroll and project management. You clock in once when you
                start working and clock out when you finish &mdash; and you can do that as many times in a day as you
                need. Checking into and leaving each store happens inside that, and your drive time, store time and
                mileage are worked out from it.
              </p>
              <div className="rounded-lg bg-slate-50 p-4">
                <MermaidDiagram chart={timeTrackingChart} id="time-tracking" />
              </div>
            </div>

            <div className="rounded-xl bg-white p-6 shadow-card">
              <h3 className="mb-4 text-lg font-semibold text-slate-800">Check In / Leave Store</h3>
              <ul className="space-y-3 text-slate-600">
                <li className="flex items-start gap-3">
                  <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Tap <strong>Clock In</strong> on the Today page before your first store visit</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Use GPS Check In when you <strong>arrive</strong> at the store (within 200 meters)</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>A green <strong>In Store</strong> banner shows on the Today page while working</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Tap <strong>Leave Store</strong> when finished, get manager signature</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Tap <strong>Clock Out</strong> after your last store visit</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Going back out later? Tap <strong>Clock In Again</strong> — you can clock in and out as many times as you need, and it all rolls up into the same day</span>
                </li>
                <li className="flex items-start gap-2">
                  <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>The app asks for your <strong>odometer</strong> at each step &mdash; clocking in, arriving, leaving, clocking out. It fills in the last reading you confirmed, so usually you just check the number and tap OK. Skip it and the mileage is simply left blank rather than guessed</span>
                </li>
                <li className="flex items-start gap-2">
                  <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Forgot to clock in, or clocked out late? Fix it on the <strong>Time</strong> tab &mdash; add the punch you missed or correct the time, and the day recalculates</span>
                </li>
              </ul>
            </div>

            <div className="rounded-xl bg-white p-6 shadow-card">
              <h3 className="mb-4 text-lg font-semibold text-slate-800">In Store Banner</h3>
              <p className="mb-3 text-slate-600">
                When you are checked into a store, a green banner appears at the top of the <strong>Today</strong> page showing:
              </p>
              <ul className="space-y-2 text-slate-600">
                <li>- The store name you are currently in</li>
                <li>- A running timer showing time at store</li>
                <li>- Tap the banner to return to the store detail page</li>
                <li>- Tap the exit icon on the banner to quickly leave the store</li>
              </ul>
            </div>

            <div className="rounded-xl bg-white p-6 shadow-card">
              <h3 className="mb-4 text-lg font-semibold text-slate-800">Viewing Time Entries</h3>
              <p className="mb-3 text-slate-600">
                The <strong>Time</strong> tab shows all your time entries for the current pay period:
              </p>
              <ul className="space-y-2 text-slate-600">
                <li>- View daily, weekly, and pay period totals</li>
                <li>- See breakdown by store and project</li>
                <li>- Edit entries if corrections are needed (before approval)</li>
                <li>- Track drive time separately from store time</li>
              </ul>
            </div>

            <div className="rounded-xl bg-amber-50 p-4 shadow-card">
              <div className="flex items-start gap-3">
                <svg className="h-6 w-6 flex-shrink-0 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  <h4 className="font-medium text-amber-800">If you forget a punch</h4>
                  <p className="mt-1 text-sm text-amber-700">
                    Leave Store when you finish at a store, and Clock Out when you finish working &mdash; they are two
                    different things. If you forget either one, you can add or correct the punch yourself on the Time
                    tab; it is kept as a correction, so nothing you already recorded is lost.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Store Visits */}
        {activeSection === 'store-visits' && (
          <div className="space-y-4">
            <div className="rounded-xl bg-white p-6 shadow-card">
              <h2 className="mb-4 text-xl font-bold text-slate-800">Store Visits</h2>
              <p className="mb-4 text-slate-600">
                Each store visit involves specific tasks depending on the project. Here is what you need to know about completing store visits.
              </p>
            </div>

            <div className="rounded-xl bg-white p-6 shadow-card">
              <h3 className="mb-4 text-lg font-semibold text-slate-800">Store Information</h3>
              <p className="mb-3 text-slate-600">
                Tap on any store card to view detailed information:
              </p>
              <ul className="space-y-2 text-slate-600">
                <li className="flex items-center gap-2">
                  <svg className="h-4 w-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>Store address and phone number</span>
                </li>
                <li className="flex items-center gap-2">
                  <svg className="h-4 w-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <span>Project details and requirements</span>
                </li>
                <li className="flex items-center gap-2">
                  <svg className="h-4 w-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                  </svg>
                  <span>Store notes from previous visits</span>
                </li>
                <li className="flex items-center gap-2">
                  <svg className="h-4 w-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Visit history</span>
                </li>
              </ul>
            </div>

            <div className="rounded-xl bg-white p-6 shadow-card">
              <h3 className="mb-4 text-lg font-semibold text-slate-800">Photos &amp; Documents</h3>
              <p className="mb-3 text-slate-600">
                Many projects require before/after photos, and some let you attach documents:
              </p>
              <ol className="space-y-2 text-slate-600">
                <li>1. Tap <strong>Add Photo</strong> (or <strong>Add Document</strong>) on the store detail page while checked in</li>
                <li>2. Take a clear photo, or choose a file from your device</li>
                <li>3. Photos are tagged with GPS and a timestamp</li>
                <li>4. Add a description if needed</li>
                <li>5. Everything is saved on your device right away and syncs automatically when you're online</li>
              </ol>
            </div>

            <div className="rounded-xl bg-white p-6 shadow-card">
              <h3 className="mb-2 text-lg font-semibold text-slate-800">Product Capture — Orders, Inventory &amp; Out of Stock</h3>
              <p className="mb-4 text-slate-600">
                What you capture at a store depends on how the project is set up. While checked in, a single button
                appears for the project's required work, and its label tells you what to do. If a project needs none
                of these, no capture button appears.
              </p>

              <div className="space-y-3">
                <div className="rounded-lg border border-slate-200 p-4">
                  <h4 className="mb-1 font-medium text-slate-800">Placing an Order</h4>
                  <p className="text-sm text-slate-600">
                    The button reads <strong>Scan / Enter Products</strong>. Scan a product barcode (or tap
                    <strong> Browse Products</strong>) and set the <strong>order quantity</strong>. Some products
                    order only in fixed multiples — shown as <strong>(x6)</strong>, for example — and the + / -
                    buttons step by that amount. The order total must reach the distributor's
                    <strong> minimum order amount</strong> before <strong>Save</strong> turns on; if it's short,
                    the screen tells you why.
                  </p>
                </div>

                <div className="rounded-lg border border-slate-200 p-4">
                  <h4 className="mb-1 font-medium text-slate-800">Counting Inventory</h4>
                  <p className="text-sm text-slate-600">
                    For inventory projects the button reads <strong>Take Inventory</strong>. Add each product and
                    enter its <strong>on-hand count</strong> — the count is required, so a product can't be added
                    without one. (On an order + inventory project you'll see both an order quantity and an on-hand
                    field on each line.)
                  </p>
                </div>

                <div className="rounded-lg border border-slate-200 p-4">
                  <h4 className="mb-1 font-medium text-slate-800">Recording Out-of-Stock</h4>
                  <p className="text-sm text-slate-600">
                    For out-of-stock projects the button reads <strong>Scan Out-of-Stock</strong>. Walk the aisle
                    and scan the <strong>shelf tag</strong> of each product that's missing — the app matches the tag
                    to the product and marks it <strong>OUT OF STOCK</strong>. On a project that also takes orders or
                    inventory, use the <strong>Products ⇄ Out of Stock</strong> toggle at the top to switch between
                    scanning products and scanning empty shelf tags.
                  </p>
                </div>

                <div className="rounded-lg border border-slate-200 p-4">
                  <h4 className="mb-1 font-medium text-slate-800">Saving</h4>
                  <p className="text-sm text-slate-600">
                    Tap <strong>Save</strong> when you're done. Your capture is stored on the device immediately and
                    syncs to the office automatically when you're online — so a weak signal in the back of a store
                    never loses your work.
                  </p>
                </div>
              </div>

              <div className="mt-4 rounded-lg bg-blue-50 p-4">
                <div className="flex items-start gap-3">
                  <svg className="h-5 w-5 flex-shrink-0 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm text-blue-700">
                    A single store visit can cover several calls or sets. Switch between them on the store page and
                    <strong> pause / resume</strong> a task without losing photos, surveys, or captured products.
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-xl bg-white p-6 shadow-card">
              <h3 className="mb-4 text-lg font-semibold text-slate-800">Completing Surveys</h3>
              <p className="mb-3 text-slate-600">
                Some stores require a survey to be completed during your visit:
              </p>
              <ul className="space-y-2 text-slate-600">
                <li>- Surveys appear on the store detail page while checked in</li>
                <li>- Answer all required questions (marked with *)</li>
                <li>- Some questions may require photo attachments</li>
                <li>- Complete the survey before tapping Leave Store</li>
              </ul>
            </div>

            <div className="rounded-xl bg-white p-6 shadow-card">
              <h3 className="mb-4 text-lg font-semibold text-slate-800">Checkout Summary</h3>
              <p className="mb-3 text-slate-600">
                When you tap <strong>Leave Store</strong>, a checkout summary appears showing:
              </p>
              <ul className="space-y-2 text-slate-600">
                <li>- Projects worked on and their completion status</li>
                <li>- Total time spent at the store</li>
                <li>- Manager name selection (from previous visits or enter new)</li>
                <li>- Signature capture area for manager to sign</li>
              </ul>
              <div className="mt-4 rounded-lg bg-blue-50 p-4">
                <div className="flex items-start gap-3">
                  <svg className="h-5 w-5 flex-shrink-0 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <h4 className="font-medium text-blue-800">Manager Not Available?</h4>
                    <p className="mt-1 text-sm text-blue-700">
                      If the manager is not available to sign, tap <strong>No Signature</strong> and select a reason (e.g., "Manager not available", "Manager refused"). This documents why a signature was not obtained.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Expenses */}
        {activeSection === 'expenses' && (
          <div className="space-y-4">
            <div className="rounded-xl bg-white p-6 shadow-card">
              <h2 className="mb-4 text-xl font-bold text-slate-800">Expense Tracking</h2>
              <p className="mb-4 text-slate-600">
                Track and submit expenses for reimbursement directly from the app. Always keep your receipts and submit expenses promptly.
              </p>
              <div className="rounded-lg bg-slate-50 p-4">
                <MermaidDiagram chart={expenseChart} id="expense-flow" />
              </div>
            </div>

            <div className="rounded-xl bg-white p-6 shadow-card">
              <h3 className="mb-4 text-lg font-semibold text-slate-800">Submitting an Expense</h3>
              <ol className="space-y-3 text-slate-600">
                <li className="flex gap-3">
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-bold text-primary-700">1</span>
                  <span>Go to the <strong>Expenses</strong> tab</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-bold text-primary-700">2</span>
                  <span>Tap <strong>Add Expense</strong></span>
                </li>
                <li className="flex gap-3">
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-bold text-primary-700">3</span>
                  <span>Select the <strong>Expense Type</strong> from the dropdown</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-bold text-primary-700">4</span>
                  <span>Enter the expense date and amount</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-bold text-primary-700">5</span>
                  <span>Take a photo of your receipt (use Camera or Gallery)</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-bold text-primary-700">6</span>
                  <span>Add a brief description</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-bold text-primary-700">7</span>
                  <span>Tap <strong>Submit Expense</strong></span>
                </li>
              </ol>
            </div>

            <div className="rounded-xl bg-white p-6 shadow-card">
              <h3 className="mb-2 text-lg font-semibold text-slate-800">Expense Types</h3>
              <p className="text-slate-600">
                The <strong>Expense Type</strong> dropdown lists the categories your office has set up — for example
                <strong> Tolls</strong>, <strong>Postage</strong>, or <strong>Other</strong>. Pick the one that best
                fits and use the <strong>description</strong> field to add detail. The list can change over time, so
                if you're unsure which type to use, check with your supervisor.
              </p>
            </div>

            <div className="rounded-xl bg-blue-50 p-4 shadow-card">
              <div className="flex items-start gap-3">
                <svg className="h-6 w-6 flex-shrink-0 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <h4 className="font-medium text-blue-800">Receipt Tips</h4>
                  <ul className="mt-2 space-y-1 text-sm text-blue-700">
                    <li>- Take photos in good lighting</li>
                    <li>- Ensure the date and amount are visible</li>
                    <li>- Submit expenses within 30 days</li>
                    <li>- Keep original receipts until approved</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Calls & Sets */}
        {activeSection === 'calls-sets' && (
          <div className="space-y-4">
            <div className="rounded-xl bg-white p-6 shadow-card">
              <h2 className="mb-4 text-xl font-bold text-slate-800">Calls & Sets</h2>
              <p className="mb-4 text-slate-600">
                Understanding the difference between <strong>Calls</strong> (regular store visits) and <strong>Sets</strong> (display installations) is key to managing your schedule effectively.
              </p>
            </div>

            <div className="rounded-xl bg-white p-6 shadow-card">
              <h3 className="mb-4 text-lg font-semibold text-slate-800">What Are Calls?</h3>
              <p className="mb-3 text-slate-600">
                Calls are your regular store visits for ongoing merchandising work. Each call has a <strong>frequency</strong> - the number of visits required per billing period (usually monthly). You choose when to complete them.
              </p>
              <div className="rounded-lg bg-cyan-50 p-4 mb-4">
                <div className="flex items-start gap-3">
                  <svg className="h-6 w-6 flex-shrink-0 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <div>
                    <h4 className="font-medium text-cyan-800">Call Progress Dashboard</h4>
                    <p className="mt-1 text-sm text-cyan-700">
                      The <strong>Schedule</strong> page shows your monthly call progress at a glance - completion percentage, pace status, and how many visits per day you need to stay on track.
                    </p>
                  </div>
                </div>
              </div>
              <div className="rounded-lg bg-green-50 p-4">
                <div className="flex items-start gap-3">
                  <svg className="h-6 w-6 flex-shrink-0 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <div>
                    <h4 className="font-medium text-green-800">My Calls Page</h4>
                    <p className="mt-1 text-sm text-green-700">
                      Access from More &gt; My Calls to see all your assigned stores, visit frequency, and detailed progress for each store.
                    </p>
                  </div>
                </div>
              </div>
              <ul className="mt-4 space-y-2 text-slate-600">
                <li className="flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-100 text-xs font-bold text-primary-700">1</span>
                  <span>View your assigned stores and required visits per month</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-100 text-xs font-bold text-primary-700">2</span>
                  <span>Track progress: "3 of 5 visits completed"</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-100 text-xs font-bold text-primary-700">3</span>
                  <span>Filter by project or sort by completion status</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-100 text-xs font-bold text-primary-700">4</span>
                  <span>Navigate between billing months</span>
                </li>
              </ul>
            </div>

            <div className="rounded-xl bg-white p-6 shadow-card">
              <h3 className="mb-4 text-lg font-semibold text-slate-800">What Are Sets?</h3>
              <p className="mb-3 text-slate-600">
                Sets are scheduled display installations or resets. Unlike calls, sets have <strong>specific dates and times</strong> and often involve team assignments.
              </p>
              <div className="rounded-lg bg-amber-50 p-4">
                <div className="flex items-start gap-3">
                  <svg className="h-6 w-6 flex-shrink-0 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  <div>
                    <h4 className="font-medium text-amber-800">My Sets Page</h4>
                    <p className="mt-1 text-sm text-amber-700">
                      Access from More &gt; My Sets to see upcoming display installations, team members, and display specifications.
                    </p>
                  </div>
                </div>
              </div>
              <ul className="mt-4 space-y-2 text-slate-600">
                <li className="flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-100 text-xs font-bold text-amber-700">1</span>
                  <span>View scheduled date, time, and estimated hours</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-100 text-xs font-bold text-amber-700">2</span>
                  <span>See team members assigned to the same set</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-100 text-xs font-bold text-amber-700">3</span>
                  <span>Review display specs (dimensions, placement, etc.)</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-100 text-xs font-bold text-amber-700">4</span>
                  <span>Filter by status: Upcoming, Today, Completed</span>
                </li>
              </ul>
            </div>

            <div className="rounded-xl bg-white p-6 shadow-card">
              <h3 className="mb-4 text-lg font-semibold text-slate-800">Schedule Page</h3>
              <p className="mb-3 text-slate-600">
                The <strong>Schedule</strong> tab shows your sets calendar and monthly call progress:
              </p>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="rounded-lg bg-slate-50 p-3 text-center">
                  <span className="text-2xl font-bold text-primary-600">Day</span>
                  <p className="text-xs text-slate-500">Single day detail</p>
                </div>
                <div className="rounded-lg bg-slate-50 p-3 text-center">
                  <span className="text-2xl font-bold text-primary-600">Week</span>
                  <p className="text-xs text-slate-500">7-day overview</p>
                </div>
                <div className="rounded-lg bg-slate-50 p-3 text-center">
                  <span className="text-2xl font-bold text-primary-600">Month</span>
                  <p className="text-xs text-slate-500">Calendar grid</p>
                </div>
              </div>

              <h4 className="mb-2 font-medium text-slate-800">Call Progress Dashboard</h4>
              <p className="mb-3 text-sm text-slate-600">
                At the top of the Schedule page, you will see your monthly call quota progress:
              </p>
              <ul className="space-y-1 text-sm text-slate-600 mb-4">
                <li>- Progress bar showing visits completed vs required</li>
                <li>- Pace indicator: <span className="text-green-600 font-medium">On track</span>, <span className="text-amber-600 font-medium">Watch pace</span>, or <span className="text-red-600 font-medium">Behind pace</span></li>
                <li>- Daily rate needed to complete on time</li>
                <li>- Days remaining in the billing period</li>
              </ul>

              <h4 className="mb-2 font-medium text-slate-800">Color-Coded Badges</h4>
              <p className="mb-2 text-sm text-slate-600">
                Calendar days show numbered badges indicating set counts:
              </p>
              <div className="flex gap-3 mb-4">
                <div className="flex items-center gap-1">
                  <span className="min-w-[20px] rounded-full bg-amber-500 px-1.5 text-center text-xs font-bold text-white">2</span>
                  <span className="text-xs text-slate-500">Pending</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="min-w-[20px] rounded-full bg-red-500 px-1.5 text-center text-xs font-bold text-white">1</span>
                  <span className="text-xs text-slate-500">Overdue</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="min-w-[20px] rounded bg-green-500 px-1.5 text-center text-xs font-bold text-white">3</span>
                  <span className="text-xs text-slate-500">Completed</span>
                </div>
              </div>

              <h4 className="mb-2 font-medium text-slate-800">Light Days Suggestion</h4>
              <p className="text-sm text-slate-600">
                The app suggests days with fewer scheduled sets - ideal for fitting in flexible call work. Look for the <span className="text-cyan-700 font-medium">Light days for calls</span> tip below the progress dashboard.
              </p>
            </div>

            <div className="rounded-xl bg-blue-50 p-4 shadow-card">
              <div className="flex items-start gap-3">
                <svg className="h-6 w-6 flex-shrink-0 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <h4 className="font-medium text-blue-800">Calls vs Sets: Key Differences</h4>
                  <table className="mt-2 w-full text-sm">
                    <thead>
                      <tr className="text-left text-blue-800">
                        <th className="pb-1">Calls</th>
                        <th className="pb-1">Sets</th>
                      </tr>
                    </thead>
                    <tbody className="text-blue-700">
                      <tr>
                        <td>Flexible timing</td>
                        <td>Specific date/time</td>
                      </tr>
                      <tr>
                        <td>Monthly frequency</td>
                        <td>One-time event</td>
                      </tr>
                      <tr>
                        <td>Individual work</td>
                        <td>Often team-based</td>
                      </tr>
                      <tr>
                        <td>Regular merchandising</td>
                        <td>Display installation</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Admin Features */}
        {activeSection === 'admin-features' && (
          <div className="space-y-4">
            <div className="rounded-xl bg-white p-6 shadow-card">
              <h2 className="mb-4 text-xl font-bold text-slate-800">Admin Features</h2>
              <p className="mb-4 text-slate-600">
                Supervisors and administrators have access to additional features for managing teams and testing the app.
              </p>
              <div className="rounded-lg bg-purple-50 p-4">
                <div className="flex items-start gap-3">
                  <svg className="h-6 w-6 flex-shrink-0 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <div>
                    <h4 className="font-medium text-purple-800">Admin Access</h4>
                    <p className="mt-1 text-sm text-purple-700">
                      These features are only visible to users with administrator privileges. If you need admin access, contact your supervisor.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-xl bg-white p-6 shadow-card">
              <h3 className="mb-4 text-lg font-semibold text-slate-800">View as User (Impersonation)</h3>
              <p className="mb-3 text-slate-600">
                Administrators can view the app as if they were another associate. This is useful for troubleshooting issues or verifying what a field rep sees.
              </p>
              <div className="space-y-3">
                <div className="rounded-lg border border-slate-200 p-4">
                  <h4 className="mb-2 font-medium text-slate-800">How to Use</h4>
                  <ol className="space-y-2 text-sm text-slate-600">
                    <li>1. Go to <strong>More</strong> page</li>
                    <li>2. Find the <strong>Admin: View as User</strong> section</li>
                    <li>3. Tap <strong>Select User to View</strong></li>
                    <li>4. Choose an associate from the list (sorted by activity)</li>
                    <li>5. The app will reload showing that user's data</li>
                  </ol>
                </div>
                <div className="rounded-lg border border-slate-200 p-4">
                  <h4 className="mb-2 font-medium text-slate-800">While Impersonating</h4>
                  <ul className="space-y-2 text-sm text-slate-600">
                    <li className="flex items-center gap-2">
                      <svg className="h-4 w-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>A purple banner appears at the top of every page</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <svg className="h-4 w-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>All data shows the impersonated user's perspective</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <svg className="h-4 w-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Tap <strong>Stop</strong> on the banner to return to your account</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="rounded-xl bg-white p-6 shadow-card">
              <h3 className="mb-4 text-lg font-semibold text-slate-800">Admin Console</h3>
              <p className="mb-3 text-slate-600">
                Access the full administration panel for system configuration and data management.
              </p>
              <div className="rounded-lg border border-slate-200 p-4">
                <h4 className="mb-2 font-medium text-slate-800">How to Access</h4>
                <ol className="space-y-2 text-sm text-slate-600">
                  <li>1. Go to <strong>More</strong> page</li>
                  <li>2. Scroll to the <strong>RDM Connect</strong> section</li>
                  <li>3. Tap <strong>Open Admin Console</strong></li>
                  <li>4. The admin panel will open in a new tab</li>
                </ol>
              </div>
            </div>

            <div className="rounded-xl bg-amber-50 p-4 shadow-card">
              <div className="flex items-start gap-3">
                <svg className="h-6 w-6 flex-shrink-0 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  <h4 className="font-medium text-amber-800">Important Notes</h4>
                  <ul className="mt-2 space-y-1 text-sm text-amber-700">
                    <li>- Actions taken while impersonating affect the actual user's data</li>
                    <li>- Use impersonation for viewing/troubleshooting only</li>
                    <li>- All impersonation activity is logged for security</li>
                    <li>- Contact IT if you need admin privileges</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Troubleshooting */}
        {activeSection === 'troubleshooting' && (
          <div className="space-y-4">
            <div className="rounded-xl bg-white p-6 shadow-card">
              <h2 className="mb-4 text-xl font-bold text-slate-800">Troubleshooting</h2>
              <p className="mb-4 text-slate-600">
                Common issues and how to resolve them. If you continue to experience problems, contact your supervisor or IT support.
              </p>
            </div>

            <div className="rounded-xl bg-white p-6 shadow-card">
              <h3 className="mb-4 text-lg font-semibold text-slate-800">Common Issues</h3>

              <div className="space-y-4">
                <details id="gps-wrong-location" className="group rounded-lg border border-amber-300 bg-amber-50">
                  <summary className="flex cursor-pointer items-center justify-between p-4 font-medium text-amber-900">
                    GPS shows wrong location (the app thinks you are far from the store)
                    <svg className="h-5 w-5 text-amber-600 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </summary>
                  <div className="border-t border-amber-200 p-4 text-sm text-slate-700">
                    <p className="mb-3">
                      If the check-in screen says you are hundreds of meters or kilometers away from a store you are actually standing in, your phone is most likely set to <strong>approximate</strong> location instead of <strong>precise</strong> location. Fix it once per device:
                    </p>
                    <h4 className="mb-1 mt-3 font-semibold text-slate-800">Android</h4>
                    <ol className="space-y-1 pl-4">
                      <li>1. Open <strong>Settings &gt; Apps</strong> and find your browser (Chrome, Edge, etc.)</li>
                      <li>2. Tap <strong>Permissions &gt; Location</strong></li>
                      <li>3. Allow location "While using the app" (or "All the time")</li>
                      <li>4. Make sure <strong>Use precise location</strong> is turned ON</li>
                    </ol>
                    <h4 className="mb-1 mt-4 font-semibold text-slate-800">iPhone / iPad (Safari)</h4>
                    <ol className="space-y-1 pl-4">
                      <li>1. Open <strong>Settings &gt; Safari &gt; Location</strong></li>
                      <li>2. Allow location access</li>
                      <li>3. Make sure <strong>Precise Location</strong> is turned ON</li>
                      <li>4. Installed home-screen apps share Safari's location setting on iOS</li>
                    </ol>
                    <h4 className="mb-1 mt-4 font-semibold text-slate-800">If it still looks wrong</h4>
                    <ol className="space-y-1 pl-4">
                      <li>1. Walk near a window or step outside briefly — indoor GPS is often weak</li>
                      <li>2. Toggle Wi-Fi off and on — phones can lock onto a stale Wi-Fi location</li>
                      <li>3. Open your phone's Maps app to confirm it shows you at the store; if Maps is also wrong, your device's positioning is the problem, not RDM Connect</li>
                      <li>4. If the issue persists at one specific store, the store's address may be mis-mapped — let your supervisor know</li>
                    </ol>
                  </div>
                </details>

                <details className="group rounded-lg border border-slate-200">
                  <summary className="flex cursor-pointer items-center justify-between p-4 font-medium text-slate-800">
                    I forgot to clock in or clock out
                    <svg className="h-5 w-5 text-slate-400 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </summary>
                  <div className="border-t border-slate-200 p-4 text-sm text-slate-600">
                    <ol className="space-y-2">
                      <li>1. Go to the <strong>Time</strong> tab &mdash; you can fix this yourself, no supervisor needed</li>
                      <li>2. Under Clock In / Out, tap the punch that is wrong to correct its time. A whole missing period gets recorded with <strong>Add time</strong> — the clock follows the visit you record</li>
                      <li>3. If you are still clocked in from earlier, the day shows as open &mdash; set the clock-out time you actually finished</li>
                      <li>4. Your totals recalculate straight away, and the change is kept as a correction so nothing you already recorded is lost</li>
                      <li>5. Once a pay period is closed for billing it can no longer be edited &mdash; ask your supervisor for those</li>
                    </ol>
                  </div>
                </details>

                <details className="group rounded-lg border border-slate-200">
                  <summary className="flex cursor-pointer items-center justify-between p-4 font-medium text-slate-800">
                    Cannot check in - GPS not working
                    <svg className="h-5 w-5 text-slate-400 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </summary>
                  <div className="border-t border-slate-200 p-4 text-sm text-slate-600">
                    <ol className="space-y-2">
                      <li>1. Make sure Location Services are enabled in your phone settings</li>
                      <li>2. Grant the RDM app permission to access your location</li>
                      <li>3. If indoors, move near a window or outside for better GPS signal</li>
                      <li>4. Make sure you are within 200 meters of the store</li>
                      <li>5. Restart the app and try again</li>
                      <li>6. If still not working, contact your supervisor</li>
                    </ol>
                  </div>
                </details>

                <details className="group rounded-lg border border-slate-200">
                  <summary className="flex cursor-pointer items-center justify-between p-4 font-medium text-slate-800">
                    Manager signature not saving
                    <svg className="h-5 w-5 text-slate-400 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </summary>
                  <div className="border-t border-slate-200 p-4 text-sm text-slate-600">
                    <ol className="space-y-2">
                      <li>1. Make sure the signature is drawn large enough on the canvas</li>
                      <li>2. Check your internet connection</li>
                      <li>3. If offline, the signature will be saved and synced when back online</li>
                      <li>4. If manager is unavailable, tap "No Signature" and provide a reason</li>
                      <li>5. Contact your supervisor if issues persist</li>
                    </ol>
                  </div>
                </details>

                <details className="group rounded-lg border border-slate-200">
                  <summary className="flex cursor-pointer items-center justify-between p-4 font-medium text-slate-800">
                    App is running slowly
                    <svg className="h-5 w-5 text-slate-400 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </summary>
                  <div className="border-t border-slate-200 p-4 text-sm text-slate-600">
                    <ol className="space-y-2">
                      <li>1. Close other apps running in the background</li>
                      <li>2. Check your internet connection</li>
                      <li>3. Try refreshing the page (pull down to refresh)</li>
                      <li>4. Clear browser cache if using web version</li>
                      <li>5. Restart your phone if issues persist</li>
                    </ol>
                  </div>
                </details>

                <details className="group rounded-lg border border-slate-200">
                  <summary className="flex cursor-pointer items-center justify-between p-4 font-medium text-slate-800">
                    Photos not uploading
                    <svg className="h-5 w-5 text-slate-400 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </summary>
                  <div className="border-t border-slate-200 p-4 text-sm text-slate-600">
                    <ol className="space-y-2">
                      <li>1. Check your internet connection (Wi-Fi or cellular)</li>
                      <li>2. Photos will sync automatically when connection is restored</li>
                      <li>3. Check that the app has permission to access your camera and photos</li>
                      <li>4. Try taking the photo again if it failed</li>
                      <li>5. Large files may take longer on slow connections</li>
                    </ol>
                  </div>
                </details>

                <details className="group rounded-lg border border-slate-200">
                  <summary className="flex cursor-pointer items-center justify-between p-4 font-medium text-slate-800">
                    Cannot log in
                    <svg className="h-5 w-5 text-slate-400 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </summary>
                  <div className="border-t border-slate-200 p-4 text-sm text-slate-600">
                    <ol className="space-y-2">
                      <li>1. Verify you are using the correct email and password</li>
                      <li>2. Use the same credentials as the RDM Connect web portal</li>
                      <li>3. Check if Caps Lock is on</li>
                      <li>4. Try the Forgot Password link if needed</li>
                      <li>5. Contact IT support if your account is locked</li>
                    </ol>
                  </div>
                </details>

                <details className="group rounded-lg border border-slate-200">
                  <summary className="flex cursor-pointer items-center justify-between p-4 font-medium text-slate-800">
                    Missing store assignments
                    <svg className="h-5 w-5 text-slate-400 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </summary>
                  <div className="border-t border-slate-200 p-4 text-sm text-slate-600">
                    <ol className="space-y-2">
                      <li>1. Pull down to refresh the Today tab</li>
                      <li>2. Check that you are viewing the correct date</li>
                      <li>3. Review the Week tab for your full schedule</li>
                      <li>4. Contact your supervisor if stores are still missing</li>
                      <li>5. Assignments may not be visible until the day before</li>
                    </ol>
                  </div>
                </details>
              </div>
            </div>

            <div className="rounded-xl bg-white p-6 shadow-card">
              <h3 className="mb-4 text-lg font-semibold text-slate-800">Still stuck? Send a Device Report</h3>
              <p className="mb-4 text-slate-600">
                The fastest way to get help is to send a <strong>device report</strong> from the app. It captures
                exactly what your device is doing — app version, GPS, recent errors, and storage state — so support
                can see the problem without a lot of back-and-forth. You can attach a screenshot and a short note too.
              </p>
              <ol className="space-y-3 text-slate-600">
                <li className="flex gap-3">
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-bold text-primary-700">1</span>
                  <span>Go to <strong>More &gt; Diagnostics</strong>.</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-bold text-primary-700">2</span>
                  <span>
                    Pick the tool that matches your problem:
                    <ul className="mt-2 space-y-1 text-sm text-slate-500">
                      <li>- <strong>Scanner / Barcode</strong> — a tag won't scan, or scans the wrong value</li>
                      <li>- <strong>GPS / Location</strong> — the app thinks you're far from the store</li>
                      <li>- <strong>Send Device Report</strong> — anything else (crash, slow, odd behavior)</li>
                    </ul>
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-bold text-primary-700">3</span>
                  <span>Add a <strong>brief summary</strong> of what went wrong (and a screenshot if it helps).</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-bold text-primary-700">4</span>
                  <span>Tap <strong>Send to support</strong> and note the <strong>reference number</strong> it gives you.</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-bold text-primary-700">5</span>
                  <span>Share that reference number with your supervisor so support can look up your device's exact state.</span>
                </li>
              </ol>
              <Link
                to="/diagnostics"
                className="mt-5 flex w-full items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-3 font-medium text-white transition-colors hover:bg-primary-700"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Open Diagnostics
              </Link>
              <div className="mt-4 rounded-lg bg-blue-50 p-4">
                <div className="flex items-start gap-3">
                  <svg className="h-5 w-5 flex-shrink-0 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm text-blue-700">
                    Send the report <strong>while the problem is happening</strong> — it captures the live state, which
                    is far more useful than describing it later.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
